import {
  AccessAvailability,
  AccessFrequency,
  AccessHours,
  AccessPermission,
  Features
} from 'src/types/graphql'

/**
 * English labels for the amenity + access enums.
 *
 * These are labels ONLY. Icons, `deprecated` flags and descriptions stay in
 * `FEATURES_MAP` (`src/constants/index.ts`) — the single source of truth for
 * the shape — and `useSpaceLabels` merges these strings over it. Adding a new
 * `Features` member therefore fails the typecheck here until an English label
 * is supplied, which is the point: it stops a Spanish label silently shipping
 * to `/en`.
 *
 * These do NOT live in a locale JSON on purpose: the consumers look values up
 * by enum key (`FEATURES_MAP[feature].label`), and routing that through `t()`
 * would need a dynamic `t(\`FEATURES.${x}\`)` template literal — explicitly
 * banned by spotme-client/CLAUDE.md → i18n, because it breaks the typed
 * `CustomTypeOptions.resources`.
 */
export const FEATURE_LABELS_EN: Record<Features, string> = {
  [Features.PrivateEntrance]: 'Private entrance',
  [Features.PrivateSpace]: 'Private space',
  [Features.SecurityCamera]: 'Security camera',
  [Features.FloorLevel]: 'Ground level',
  [Features.PetFree]: 'Pet free',
  [Features.SmokeDetectors]: 'Smoke detector',
  [Features.Fumigated]: 'Fumigated',
  [Features.ClimateControl]: 'Climate control',
  [Features.Surveillance]: 'Surveillance',
  [Features.VehicleAccess]: 'Vehicle access',
  [Features.Elevator]: 'Elevator',
  [Features.AlarmSystem]: 'Alarm system',
  [Features.AntiFireSystem]: 'Fire suppression system',
  [Features.Mezzanine]: 'Mezzanine',
  [Features.Platform]: 'Loading dock',
  [Features.Water]: 'Water and drainage',
  [Features.TruckOrTrailerAccess]: 'Truck / trailer access',
  [Features.Railyard]: 'Maneuvering yard',
  [Features.Gas]: 'Gas',
  [Features.Toilets]: 'Restrooms',
  [Features.ElectricalInstallation]:
    'Electrical installation (lighting, outlets, switches)',
  [Features.Shelves]: 'Shelving / racking',
  [Features.Reception]: 'Receptionist',
  [Features.Offices]: 'Offices',
  [Features.Cafeteria]: 'Cafeteria',
  [Features.Tank]: 'Cistern',
  [Features.LockedArea]: 'Padlock',
  [Features.CodeAccess]: 'Code access',
  [Features.KeyAccess]: 'Key access',
  [Features.Waterproofed]: 'Waterproofed',
  [Features.Tinaco]: 'Roof water tank',
  [Features.Stairs]: 'Stairs',
  [Features.ArmoredSpace]: 'Armored space',
  // Deprecated members — still mapped so the Record stays exhaustive and so a
  // legacy listing that carries one renders in English rather than falling
  // back to Spanish.
  [Features.Light]: 'Electrical installation',
  [Features.MeetingRooms]: 'Meeting room',
  [Features.AirConditioner]: 'Air conditioning',
  [Features.ClassificationYard]: 'Maneuvering yard',
  [Features.IndependentEntrance]: 'Independent entrance',
  [Features.PestControl]: 'Pest control',
  [Features.Bathrooms]: 'Restrooms',
  [Features.ShieldedSpace]: 'Covered space'
}

export const ACCESS_PERMISSION_LABELS_EN: Record<AccessPermission, string> = {
  [AccessPermission.Notice]: 'Yes (by appointment)',
  [AccessPermission.NoNotice]: 'No (no appointment needed)'
}

export const ACCESS_FREQUENCY_LABELS_EN: Record<AccessFrequency, string> = {
  [AccessFrequency.MonToFri]: 'Monday to Friday only',
  [AccessFrequency.MonToSat]: 'Monday to Saturday only',
  [AccessFrequency.FriToSun]: 'Friday to Sunday only',
  [AccessFrequency.Weekends]: 'Saturday and Sunday only',
  [AccessFrequency.Anyday]: 'Any day of the week'
}

export const ACCESS_HOURS_LABELS_EN: Record<AccessHours, string> = {
  [AccessHours.Anytime]: 'Any time',
  [AccessHours.DaytimeHours]: 'Business hours (9am - 6pm)',
  [AccessHours.ExtendedHours]: 'Daytime (8am - 8pm)',
  [AccessHours.EveningHours]: 'Evenings (6pm onwards)'
}

export const ACCESS_AVAILABILITY_LABELS_EN: Record<AccessAvailability, string> =
  {
    [AccessAvailability.Daily]: 'Daily',
    [AccessAvailability.Weekly]: 'Weekly (a few times / week)',
    [AccessAvailability.Monthly]: 'Monthly (a few times / month)',
    [AccessAvailability.Unfrequently]: 'Infrequently (less than once / month)'
  }
